import React, { useState, useCallback, useEffect, useContext, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { Localization, AppMessagesContext, SessionContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow, closeWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassButton } from "src/components/GlassButtons";
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "src/components/MitGlassComponents";
import { useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import type { LLMAnalysisResult, LLMAnalysisResultItem } from "./ANNLLMAnalyzer";
import { cutPdfToSelectedPages } from "./ANNPDFCutter";
import { renderPdfPageToImage } from "./ANNPDFPageToImage";
import { uploadOverlayImage, deriveOverlayPlacement, blobToDataUrl } from "./ANNImageOverlayStorage";
import { AnnotationType, AnnotationContextType, ANNOTATION_TYPES, type AnnotationTypeCategory, AnnotationQuality, type ANNAnnotationRecord } from "./ANNTypes";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { useANNEditorContext, ANNEditorActionType } from "./ANNEditorState";
import { useDeleteAnnotation, useSaveAnnotation } from "./ANNState";
import { ANNManager } from "./ANNManager";
import {
   buildDocumentLlmAnalysis,
   buildOverwriteServitutTextDialogMessage,
   buildConsecutivePages,
   canRestoreAiPageSuggestion,
   clampPagesToDocument,
   findAllServitutDocumentAnnotations,
   mergeDocumentPayloadWithLlmAnalysis,
   normalizeAnnotationTypeValue,
   normalizePages,
   resolveAktPagesForSave,
   resolveAnalysisRelevantPages,
   resolvePagesToPersistOnViewerClose,
   type PdfSourceKind,
} from "./ANNServitutDocumentAnnotations";
import { appendSuffixToPdfFileName } from "src/managers/ServitutDownloadNaming";
import {
   MAX_PDF_FIND_MATCHES,
   PDF_SEARCH_PAGES_PER_SLICE,
   findMatchKey,
   findMatchesInPageItems,
   fitPageWidthScale,
   highlightTextItemHtml,
   mountedPagesAround,
   mountedWindowSizeForViewport,
   normalizeFindQuery,
   pageFromScrollOffset,
   pdfPageSlotHeight,
   rangesForTextItem,
   samePageSet,
   scrollOffsetForPage,
   virtualPdfStackHeight,
   type PdfFindMatch,
} from "./ANNPDFSearch";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { ANNAnalysisResultPanel } from "./ANNAnalysisResultPanel";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ANNPDFViewerProps {
   managedWindow: ManagedWindow;
}

interface WindowParams {
   pdfUrl: string;
   pdfBlob?: Blob;
   analysisResult?: LLMAnalysisResult;
   aktId?: string;
   documentId?: string;
   bfeNumber?: string | number;
   contextType?: AnnotationContextType;
   organizationId?: string;
   userId?: string;
   userName?: string;
   datoloebenr?: string;
   documentFilename?: string;
   /** Servitut heading shown with dato-løbenummer so reviewers know what to look for. */
   documentHeading?: string;
   /** From servitutredegørelse: skip the save form, confirm page changes, then approve the row. */
   saveAndApprove?: boolean;
   initialSelectedPages?: number[];
   /** Whether the opened blob is a full akt or an already-cut servituttekst annotation. */
   pdfSourceKind?: PdfSourceKind;
   /** Original akt page numbers corresponding to pages 1..N of a cut annotation PDF. */
   sourceExtractedPages?: number[];
   onCloseSelectedPages?: (pages: number[]) => void;
   /** Called after a successful "Gem" annotation save (before the window closes). */
   onDocumentSaved?: (pages: number[]) => void;
   /** Newer/digital servituts: empty AI pages_kept means the whole document. */
   treatEmptyPagesKeptAsAll?: boolean;
   /** Phase A shows only the summary; B and other callers show the full analysis. */
   analysisDisplayMode?: "summary" | "full";
}

function SimpleButton({
   children,
   onClick,
   disabled,
   selected,
   variant,
   title,
   className
}: {
   children: React.ReactNode;
   onClick?: () => void;
   disabled?: boolean;
   selected?: boolean;
   variant?: 'default' | 'success' | 'primary' | 'muted';
   title?: string;
   className?: string;
}) {
   const variantClass =
      variant === "success" ? "ann-pdf-btn--success" :
         variant === "primary" ? "ann-pdf-btn--primary" :
            variant === "muted" ? "ann-pdf-btn--muted" : "";
   const selectedClass = selected ? "ann-pdf-btn--selected" : "";
   const classes = ["ann-pdf-btn", variantClass, selectedClass, className].filter(Boolean).join(" ");

   return (
      <button
         onClick={onClick}
         disabled={disabled}
         title={title}
         className={classes}
      >
         {children}
      </button>
   );
}

const PageSelectionGrid = React.memo(React.forwardRef<HTMLDivElement, {
   numPages: number;
   selectedPages: ReadonlySet<number>;
   suggestedKey: string;
   suggestedPages: readonly number[];
   onJump: (page: number) => void;
}>(function PageSelectionGrid({ numPages, selectedPages, suggestedPages, onJump }, ref) {
   const suggestedSet = useMemo(() => new Set(suggestedPages), [suggestedPages]);
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");

   return (
      <div
         ref={ref}
         className="ann-pdf-pageSelectionButtons"
         onClick={(event) => {
            const button = (event.target as HTMLElement).closest<HTMLElement>("[data-ann-page-btn]");
            if (!button) {
               return;
            }
            const page = Number(button.dataset.annPageBtn);
            if (Number.isFinite(page)) {
               onJump(page);
            }
         }}
      >
         {Array.from({ length: numPages }, (_, index) => {
            const pageNum = index + 1;
            const selected = selectedPages.has(pageNum);
            const suggested = suggestedSet.has(pageNum);
            const classes = [
               "ann-pdf-pageBtn",
               selected ? "ann-pdf-pageBtn--selected" : "",
               suggested ? "ann-pdf-pageBtn--suggested" : "",
            ].filter(Boolean).join(" ");
            return (
               <button
                  key={pageNum}
                  type="button"
                  data-ann-page-btn={pageNum}
                  className={classes}
                  title={suggested ? `${aiSuggestedLabel} - Page ${pageNum}` : `Page ${pageNum}`}
               >
                  {pageNum}
               </button>
            );
         })}
      </div>
   );
}), (prev, next) => (
   prev.numPages === next.numPages
   && prev.selectedPages === next.selectedPages
   && prev.suggestedKey === next.suggestedKey
   && prev.onJump === next.onJump
));

function ControlButton({
   children,
   onClick,
   disabled,
   title
}: {
   children: React.ReactNode;
   onClick?: () => void;
   disabled?: boolean;
   title?: string;
}) {
   return (
      <button
         onClick={onClick}
         disabled={disabled}
         title={title}
         className="ann-pdf-controlBtn"
      >
         {children}
      </button>
   );
}

function SaveDocumentModal({
   open,
   onClose,
   onSave,
   saving,
   canVerify,
   defaultTitle,
   defaultHistoryComment
}: {
   open: boolean;
   onClose: () => void;
   onSave: (annotationType: AnnotationTypeCategory, quality: AnnotationQuality | undefined, verifyStamp: boolean, title: string, historyComment: string) => void;
   saving: boolean;
   canVerify: boolean;
   defaultTitle?: string;
   defaultHistoryComment?: string;
}) {
   const [annotationType, setAnnotationType] = useState<AnnotationTypeCategory>("Dokument");
   const [quality, setQuality] = useState<AnnotationQuality | undefined>(undefined);
   const [verifyStamp, setVerifyStamp] = useState(false);
   const [title, setTitle] = useState(defaultTitle || "");
   const [historyComment, setHistoryComment] = useState(defaultHistoryComment || "");

   useEffect(() => {
      if (defaultTitle) setTitle(defaultTitle);
      if (defaultHistoryComment) setHistoryComment(defaultHistoryComment);
   }, [defaultTitle, defaultHistoryComment]);

   if (!open) return null;

   return ReactDOM.createPortal(
      <AdvancedDragModal
         title={Localization.getText("Save Document Annotation")}
         allowFullscreen={false}
         allowUserMiniMize={false}
         PosDefault={{ left: "30%", top: "20%", width: "480px" }}
         onClose={onClose}
      >
         <ADModalBody>
            <div className="ann-create-form-container">
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Annotation Type")}
                  </label>
                  <select
                     value={annotationType}
                     onChange={e => setAnnotationType(e.target.value as AnnotationTypeCategory)}
                     className="ann-create-select"
                  >
                     {ANNOTATION_TYPES.map(type => (
                        <option key={type} value={type}>{Localization.getText(type)}</option>
                     ))}
                  </select>
               </div>

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Quality Assessment")}
                  </label>
                  <div className="ann-create-quality-selector-group">
                     <div
                        onClick={() => setQuality(undefined)}
                        className={`ann-create-quality-selector ann-quality-none ${quality === undefined ? "ann-create-quality-selector-none ann-create-quality-selector-selected" : ""}`}
                     >
                        <div className={`ann-create-quality-indicator-circle ${quality === undefined ? "ann-create-quality-indicator-circle-selected" : ""}`} />
                        <span className={`ann-create-quality-label ${quality === undefined ? "ann-create-quality-label-selected" : ""}`}>{Localization.getText("None")}</span>
                     </div>
                     {[AnnotationQuality.Gold, AnnotationQuality.Silver, AnnotationQuality.Bronze].map((q) => {
                        const isSelectedQ = quality === q;
                        const label = q === AnnotationQuality.Gold ? Localization.getText("Gold")
                           : q === AnnotationQuality.Silver ? Localization.getText("Silver")
                              : Localization.getText("Bronze");
                        return (
                           <div
                              key={q}
                              onClick={() => setQuality(q)}
                              className={`ann-create-quality-selector ${isSelectedQ ? "ann-create-quality-selector-selected" : ""} ann-quality-${q.toLowerCase()}`}
                           >
                              <div className={`ann-create-quality-indicator-circle ${isSelectedQ ? "ann-create-quality-indicator-circle-selected" : ""}`} />
                              <span className={`ann-create-quality-label ${isSelectedQ ? "ann-create-quality-label-selected" : ""}`}>{label}</span>
                           </div>
                        );
                     })}
                  </div>
               </div>

               {canVerify && (
                  <div>
                     <label
                        className={`ann-create-verifyCheckboxLabel ${verifyStamp ? "ann-create-verifyCheckboxLabel--checked" : ""}`}
                     >
                        <input
                           type="checkbox"
                           checked={verifyStamp}
                           onChange={(e) => setVerifyStamp(e.target.checked)}
                           className="ann-create-verifyCheckbox"
                        />
                        <MIBIcon icon="userVerified" />
                        <span className="ann-create-verifyText">
                           {Localization.getText("Stamp this annotation as verified")}
                        </span>
                     </label>
                  </div>
               )}

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Title")}
                  </label>
                  <input
                     value={title}
                     onChange={(e) => setTitle(e.target.value)}
                     className="ann-create-input"
                  />
               </div>

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("History comment")}
                  </label>
                  <textarea
                     rows={3}
                     value={historyComment}
                     onChange={(e) => setHistoryComment(e.target.value)}
                     className="ann-create-textarea"
                     placeholder={Localization.getText("Optional history comment")}
                  />
               </div>

               <div className="ann-create-button-group">
                  <GlassButton disabled={saving || !title.trim()} onClick={() => onSave(annotationType, quality, verifyStamp, title, historyComment)}>
                     {saving ? Localization.getText("Saving...") : Localization.getText("Save")}
                  </GlassButton>
                  <GlassButton onClick={onClose}>{Localization.getText("Cancel")}</GlassButton>
               </div>
            </div>
         </ADModalBody>
      </AdvancedDragModal>,
      document.body
   );
}

export function ANNPDFViewer({ managedWindow }: ANNPDFViewerProps) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { window: portalWindow } = useFloatingPortalContext();
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: mapitStateDispatch, state: mapitState } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: editorDispatch } = useANNEditorContext();
   const saveAnnotation = useSaveAnnotation();
   const deleteAnnotation = useDeleteAnnotation();
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const canWriteAnnotation = hasAccessToFeature(Feature.AnnotationWrite);
   const params = managedWindow.windowParams as WindowParams | undefined;

   const [numPages, setNumPages] = useState<number>(0);
   const [currentPage, setCurrentPage] = useState<number>(1);
   const [scale, setScale] = useState<number>(1.0);
   const [zoomMode, setZoomMode] = useState<"fitWidth" | "manual">("fitWidth");
   const [basePageWidth, setBasePageWidth] = useState(0);
   const [loading, setLoading] = useState<boolean>(true);
   const [error, setError] = useState<string | null>(null);
   const [pdfData, setPdfData] = useState<string | ArrayBuffer | null>(null);

   const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
   const [saving, setSaving] = useState<boolean>(false);
   const [uploadingPage, setUploadingPage] = useState<number | null>(null);
   const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
   const displayRef = useRef<HTMLDivElement | null>(null);
   const pageButtonListRef = useRef<HTMLDivElement | null>(null);
   const didInitialAiScrollRef = useRef(false);
   const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
   const scaleRef = useRef(scale);
   const scrollActivityRef = useRef(false);
   const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const bumpScrollActivity = useCallback(() => {
      scrollActivityRef.current = true;
      if (scrollIdleTimerRef.current != null) {
         clearTimeout(scrollIdleTimerRef.current);
      }
      scrollIdleTimerRef.current = setTimeout(() => {
         scrollActivityRef.current = false;
         scrollIdleTimerRef.current = null;
      }, 450);
   }, []);

   /** Estimated rendered page height at current scale (from page 1); used for off-screen placeholders. */
   const [estimatedPageHeight, setEstimatedPageHeight] = useState<number | null>(null);
   /** Page numbers with an active <Page /> (near viewport). */
   const [mountedPdfPages, setMountedPdfPages] = useState<Set<number>>(() => new Set());

   const findInputRef = useRef<HTMLInputElement | null>(null);
   const findGenerationRef = useRef(0);
   const findLastBuiltQueryRef = useRef("");
   const findMatchesRef = useRef<PdfFindMatch[]>([]);
   const [findQuery, setFindQuery] = useState("");
   const [findMatches, setFindMatches] = useState<PdfFindMatch[]>([]);
   const [findScope, setFindScope] = useState<"all" | "selected">("all");
   const [findMatchIndex, setFindMatchIndex] = useState(-1);
   const pageWidthRef = useRef(0);
   const zoomModeRef = useRef<"fitWidth" | "manual">("fitWidth");
   const currentPageRef = useRef(1);
   const findNavUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const resizeFitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
   const pendingFindIndexRef = useRef(-1);
   const pinnedFindPageRef = useRef<number | null>(null);
   const pendingFindRevealRef = useRef(false);
   const findNavLockRef = useRef(false);
   const mountedPdfPagesRef = useRef<Set<number>>(new Set());
   const estimatedPageHeightRef = useRef<number | null>(null);
   const [findResultId, setFindResultId] = useState(0);
   const [findSearchStatus, setFindSearchStatus] = useState<"idle" | "searching" | "done">("idle");
   const [findCapped, setFindCapped] = useState(false);
   const [findProgress, setFindProgress] = useState<{ done: number; total: number } | null>(null);

   findMatchesRef.current = findMatches;
   mountedPdfPagesRef.current = mountedPdfPages;
   estimatedPageHeightRef.current = estimatedPageHeight;

   const analysisItem: LLMAnalysisResultItem | undefined = params?.analysisResult?.items?.[0];
   const treatEmptyPagesKeptAsAll = Boolean(params?.treatEmptyPagesKeptAsAll);
   const pagesKept = (() => {
      const resolved = resolveAnalysisRelevantPages(
         analysisItem?.pages_kept,
         analysisItem ? numPages : 0,
         treatEmptyPagesKeptAsAll,
      );
      return numPages > 0 ? clampPagesToDocument(resolved, numPages) : resolved;
   })();
   const pagesKeptKey = pagesKept.join(",");
   const canRestoreAiPages = canRestoreAiPageSuggestion({
      aiPagesKept: analysisItem?.pages_kept,
      pdfSourceKind: params?.pdfSourceKind,
   });
   const initialSelectedPages = params?.initialSelectedPages;
   const initialSelectedPagesKey = initialSelectedPages?.join(",") ?? "";
   const documentOptions = useMemo(
      () => ({ ownerDocument: portalWindow.document }),
      [portalWindow]
   );

   useEffect(() => {
      const source = Array.isArray(initialSelectedPages) && initialSelectedPages.length > 0
         ? initialSelectedPages
         : pagesKept;
      if (!source.length) {
         return;
      }
      const next = numPages > 0 ? clampPagesToDocument(source, numPages) : normalizePages(source);
      if (!next.length && treatEmptyPagesKeptAsAll && numPages > 0) {
         setSelectedPages(new Set(buildConsecutivePages(numPages)));
         return;
      }
      if (next.length) {
         setSelectedPages(new Set(next));
      }
   }, [initialSelectedPagesKey, pagesKeptKey, numPages, treatEmptyPagesKeptAsAll]);

   useEffect(() => {
      if (params?.pdfBlob) {
         const reader = new FileReader();
         reader.onload = () => {
            setPdfData(reader.result);
         };
         reader.onerror = () => {
            setError("Failed to read PDF data");
         };
         reader.readAsArrayBuffer(params.pdfBlob);
      } else if (params?.pdfUrl) {
         setPdfData(params.pdfUrl);
      }
   }, [params?.pdfBlob, params?.pdfUrl]);

   useEffect(() => {
      didInitialAiScrollRef.current = false;
   }, [pdfData]);

   useEffect(() => {
      scaleRef.current = scale;
   }, [scale]);

   useEffect(() => {
      zoomModeRef.current = zoomMode;
   }, [zoomMode]);

   useEffect(() => {
      currentPageRef.current = currentPage;
   }, [currentPage]);

   useEffect(() => {
      const root = pageButtonListRef.current;
      if (!root || currentPage < 1) {
         return;
      }
      root.querySelector(".ann-pdf-pageBtn--current")?.classList.remove("ann-pdf-pageBtn--current");
      const currentBtn = root.querySelector<HTMLElement>(`[data-ann-page-btn="${currentPage}"]`);
      currentBtn?.classList.add("ann-pdf-pageBtn--current");
      currentBtn?.scrollIntoView({ block: "nearest", inline: "nearest" });
   }, [currentPage, numPages]);

   useEffect(() => {
      findGenerationRef.current += 1;
      findLastBuiltQueryRef.current = "";
      pendingFindIndexRef.current = -1;
      pinnedFindPageRef.current = null;
      pendingFindRevealRef.current = false;
      findNavLockRef.current = false;
      if (findNavUnlockTimerRef.current != null) {
         clearTimeout(findNavUnlockTimerRef.current);
         findNavUnlockTimerRef.current = null;
      }
      if (resizeFitTimerRef.current != null) {
         clearTimeout(resizeFitTimerRef.current);
         resizeFitTimerRef.current = null;
      }
      setFindQuery("");
      setFindMatches([]);
      setFindMatchIndex(-1);
      setFindResultId(0);
      setFindSearchStatus("idle");
      setFindCapped(false);
      setFindProgress(null);
      setFindScope("all");
      pageWidthRef.current = 0;
      setBasePageWidth(0);
      setZoomMode("fitWidth");
      setMountedPdfPages(new Set());
      setEstimatedPageHeight(null);
      pdfDocRef.current = null;
   }, [pdfData]);

   useEffect(() => {
      return () => {
         findGenerationRef.current += 1;
         if (findNavUnlockTimerRef.current != null) {
            clearTimeout(findNavUnlockTimerRef.current);
         }
         if (resizeFitTimerRef.current != null) {
            clearTimeout(resizeFitTimerRef.current);
         }
      };
   }, []);

   const onDocumentLoadSuccess = useCallback(async (pdf: PDFDocumentProxy) => {
      pdfDocRef.current = pdf;
      setNumPages(pdf.numPages);
      setLoading(false);

      const anchor = pagesKept.length > 0 ? pagesKept[0] : 1;
      setMountedPdfPages(new Set(mountedPagesAround(anchor, pdf.numPages)));

      if (pagesKept.length > 0) {
         setCurrentPage(pagesKept[0]);
      }

      try {
         const p1 = await pdf.getPage(1);
         const unscaled = p1.getViewport({ scale: 1 });
         pageWidthRef.current = unscaled.width;
         setBasePageWidth(unscaled.width);
         const fitted = fitPageWidthScale(displayRef.current?.clientWidth || 0, unscaled.width);
         if (zoomModeRef.current === "fitWidth") {
            setScale(fitted);
            setEstimatedPageHeight(p1.getViewport({ scale: fitted }).height);
         } else {
            setEstimatedPageHeight(p1.getViewport({ scale: scaleRef.current }).height);
         }
      } catch {
         setEstimatedPageHeight(800);
      }
   }, [pagesKeptKey]);

   useEffect(() => {
      const pdf = pdfDocRef.current;
      if (!pdf || numPages === 0) {
         return;
      }
      let cancelled = false;
      pdf
         .getPage(1)
         .then((p) => {
            if (cancelled) {
               return;
            }
            const v = p.getViewport({ scale });
            setEstimatedPageHeight(v.height);
         })
         .catch(() => {
            if (!cancelled) {
               setEstimatedPageHeight(800);
            }
         });
      return () => {
         cancelled = true;
      };
   }, [scale, numPages]);

   const onDocumentLoadError = useCallback((error: Error) => {
      setError(`Failed to load PDF: ${error.message}`);
      setLoading(false);
   }, []);

   const jumpToPdfPage = useCallback((pageNum: number, behavior: ScrollBehavior = "auto", scrollPage = true) => {
      const container = displayRef.current;
      if (!container || pageNum < 1 || numPages < 1) {
         return;
      }
      const slot = pdfPageSlotHeight(estimatedPageHeightRef.current ?? 480);
      const windowSize = mountedWindowSizeForViewport(container.clientHeight, slot);
      const next = new Set(mountedPagesAround(pageNum, numPages, windowSize));
      if (pinnedFindPageRef.current) {
         next.add(pinnedFindPageRef.current);
      }
      setMountedPdfPages((prev) => (samePageSet(prev, next) ? prev : next));
      setCurrentPage(pageNum);
      if (!scrollPage) {
         return;
      }
      const top = scrollOffsetForPage(pageNum, slot);
      if (behavior === "smooth") {
         container.scrollTo({ top, behavior: "smooth" });
      } else {
         container.scrollTop = top;
      }
   }, [numPages]);

   const scrollToPdfPage = jumpToPdfPage;

   const applyFitWidth = useCallback(() => {
      if (findNavLockRef.current) {
         return;
      }
      const pageWidth = pageWidthRef.current;
      const containerWidth = displayRef.current?.clientWidth || 0;
      if (!pageWidth || !containerWidth) {
         return;
      }
      const next = fitPageWidthScale(containerWidth, pageWidth);
      setZoomMode("fitWidth");
      if (Math.abs(next - scaleRef.current) > 0.04) {
         setScale(next);
      }
   }, []);

   const lockFindNavigation = useCallback((holdMs = 1200) => {
      findNavLockRef.current = true;
      bumpScrollActivity();
      if (findNavUnlockTimerRef.current != null) {
         clearTimeout(findNavUnlockTimerRef.current);
      }
      findNavUnlockTimerRef.current = setTimeout(() => {
         findNavLockRef.current = false;
         findNavUnlockTimerRef.current = null;
      }, holdMs);
   }, [bumpScrollActivity]);

   const ensureFindPageMounted = useCallback((pageNum: number) => {
      if (pageNum < 1 || numPages < 1) {
         return;
      }
      pinnedFindPageRef.current = pageNum;
      jumpToPdfPage(pageNum, "auto", true);
   }, [jumpToPdfPage, numPages]);

   const syncFindHitClasses = useCallback(() => {
      const root = displayRef.current;
      if (!root) {
         return null;
      }
      const match = findMatchesRef.current[pendingFindIndexRef.current];
      const key = match ? findMatchKey(match) : "";
      root.querySelectorAll<HTMLElement>("mark[data-ann-find]").forEach((el) => {
         el.classList.toggle("ann-pdf-findHit--current", Boolean(key) && el.dataset.annFind === key);
      });
      return match ?? null;
   }, []);

   const revealCurrentFindHit = useCallback((): boolean => {
      const match = syncFindHitClasses();
      const container = displayRef.current;
      const mark = container?.querySelector<HTMLElement>(".ann-pdf-findHit--current");
      if (!container || !mark || !match) {
         return false;
      }
      const area = container.getBoundingClientRect();
      const hit = mark.getBoundingClientRect();
      const pad = 48;
      const offscreen =
         hit.top < area.top + pad
         || hit.bottom > area.bottom - pad
         || hit.left < area.left + 8
         || hit.right > area.right - 8;
      if (!offscreen) {
         return true;
      }
      lockFindNavigation();
      container.scrollTop += hit.top - area.top - container.clientHeight / 2 + hit.height / 2;
      container.scrollLeft += hit.left - area.left - Math.min(container.clientWidth / 2, 80);
      return true;
   }, [lockFindNavigation, syncFindHitClasses]);

   const runPdfSearch = useCallback(
      async (qRaw: string, scope: "all" | "selected") => {
         const pdf = pdfDocRef.current;
         const q = normalizeFindQuery(qRaw);
         if (!pdf || !q) {
            findMatchesRef.current = [];
            pendingFindIndexRef.current = -1;
            pinnedFindPageRef.current = null;
            pendingFindRevealRef.current = false;
            setFindMatches([]);
            setFindMatchIndex(-1);
            setFindResultId((id) => id + 1);
            setFindSearchStatus("idle");
            setFindCapped(false);
            setFindProgress(null);
            return;
         }
         const pages = scope === "selected"
            ? Array.from(selectedPages).filter((page) => page >= 1 && page <= pdf.numPages).sort((a, b) => a - b)
            : Array.from({ length: pdf.numPages }, (_, index) => index + 1);
         if (!pages.length) {
            appMessageDispatch(actionSetInfoMessage(Localization.getText("Select pages to search")));
            return;
         }
         const gen = ++findGenerationRef.current;
         findLastBuiltQueryRef.current = `${scope}:${q}:${scope === "selected" ? pages.join(",") : ""}`;
         setFindScope(scope);
         setFindSearchStatus("searching");
         setFindCapped(false);
         setFindProgress({ done: 0, total: pages.length });
         const matches: PdfFindMatch[] = [];
         let capped = false;
         try {
            for (let i = 0; i < pages.length; i++) {
               if (gen !== findGenerationRef.current) {
                  return;
               }
               const pageNumber = pages[i];
               const page = await pdf.getPage(pageNumber);
               const tc = await page.getTextContent();
               const pageMatches = findMatchesInPageItems(pageNumber, tc.items, q);
               for (const match of pageMatches) {
                  if (matches.length >= MAX_PDF_FIND_MATCHES) {
                     capped = true;
                     break;
                  }
                  matches.push(match);
               }
               if (capped) {
                  break;
               }
               if ((i + 1) % PDF_SEARCH_PAGES_PER_SLICE === 0) {
                  if ((i + 1) % (PDF_SEARCH_PAGES_PER_SLICE * 3) === 0) {
                     setFindProgress({ done: i + 1, total: pages.length });
                  }
                  await new Promise((r) => setTimeout(r, 0));
               }
            }
         } catch {
            if (gen !== findGenerationRef.current) {
               return;
            }
            findMatchesRef.current = [];
            pendingFindIndexRef.current = -1;
            pinnedFindPageRef.current = null;
            pendingFindRevealRef.current = false;
            setFindMatches([]);
            setFindMatchIndex(-1);
            setFindResultId((id) => id + 1);
            setFindSearchStatus("done");
            setFindCapped(false);
            setFindProgress(null);
            return;
         }
         if (gen !== findGenerationRef.current) {
            return;
         }
         findMatchesRef.current = matches;
         pendingFindIndexRef.current = matches.length > 0 ? 0 : -1;
         pendingFindRevealRef.current = matches.length > 0;
         setFindResultId((id) => id + 1);
         setFindMatches(matches);
         setFindMatchIndex(matches.length > 0 ? 0 : -1);
         setFindCapped(capped);
         setFindSearchStatus("done");
         setFindProgress(null);
         if (matches[0]) {
            lockFindNavigation();
            ensureFindPageMounted(matches[0].page);
         } else {
            pinnedFindPageRef.current = null;
         }
      },
      [appMessageDispatch, ensureFindPageMounted, lockFindNavigation, selectedPages]
   );

   const goToFindIndex = useCallback((index: number) => {
      const matches = findMatchesRef.current;
      if (!matches.length) {
         return;
      }
      const next = ((index % matches.length) + matches.length) % matches.length;
      pendingFindIndexRef.current = next;
      setFindMatchIndex(next);
      const match = matches[next];
      if (!match) {
         return;
      }
      pendingFindRevealRef.current = true;
      lockFindNavigation();
      ensureFindPageMounted(match.page);
      requestAnimationFrame(() => {
         if (revealCurrentFindHit()) {
            pendingFindRevealRef.current = false;
         }
      });
   }, [ensureFindPageMounted, lockFindNavigation, revealCurrentFindHit]);

   const goFindNext = useCallback(() => {
      const matches = findMatchesRef.current;
      if (matches.length === 0) {
         return;
      }
      const current = pendingFindIndexRef.current;
      goToFindIndex(current < 0 ? 0 : current + 1);
   }, [goToFindIndex]);

   const goFindPrev = useCallback(() => {
      const matches = findMatchesRef.current;
      if (matches.length === 0) {
         return;
      }
      const current = pendingFindIndexRef.current;
      goToFindIndex(current <= 0 ? matches.length - 1 : current - 1);
   }, [goToFindIndex]);

   const onFindSubmit = useCallback((scope: "all" | "selected" = findScope) => {
      const q = findQuery.trim();
      const selectedKey = scope === "selected"
         ? Array.from(selectedPages).sort((a, b) => a - b).join(",")
         : "";
      const searchKey = `${scope}:${normalizeFindQuery(q)}:${selectedKey}`;
      if (!q) {
         findGenerationRef.current += 1;
         findLastBuiltQueryRef.current = "";
         pendingFindIndexRef.current = -1;
         pinnedFindPageRef.current = null;
         pendingFindRevealRef.current = false;
         setFindMatches([]);
         setFindMatchIndex(-1);
         setFindResultId((id) => id + 1);
         setFindSearchStatus("idle");
         setFindCapped(false);
         setFindProgress(null);
         return;
      }
      if (findLastBuiltQueryRef.current === searchKey && findMatchesRef.current.length > 0) {
         goFindNext();
         return;
      }
      void runPdfSearch(q, scope);
   }, [findQuery, findScope, goFindNext, runPdfSearch, selectedPages]);

   useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
         if ((e.ctrlKey || e.metaKey) && e.key === "f") {
            e.preventDefault();
            findInputRef.current?.focus();
            findInputRef.current?.select();
         }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, []);

   const goToPrevPage = useCallback(() => {
      jumpToPdfPage(Math.max(1, currentPageRef.current - 1), "smooth");
   }, [jumpToPdfPage]);

   const goToNextPage = useCallback(() => {
      jumpToPdfPage(Math.min(numPages, currentPageRef.current + 1), "smooth");
   }, [jumpToPdfPage, numPages]);

   const zoomIn = useCallback(() => {
      setZoomMode("manual");
      setScale(prev => Math.min(3, prev + 0.25));
   }, []);

   const zoomOut = useCallback(() => {
      setZoomMode("manual");
      setScale(prev => Math.max(0.35, prev - 0.25));
   }, []);

   const isAISuggestedPage = (page: number): boolean => {
      return pagesKept.includes(page);
   };

   const isSelectedPage = (page: number): boolean => {
      return selectedPages.has(page);
   };

   const togglePageSelection = useCallback((page: number) => {
      setSelectedPages(prev => {
         const newSet = new Set(prev);
         if (newSet.has(page)) {
            newSet.delete(page);
         } else {
            newSet.add(page);
         }
         return newSet;
      });
   }, []);

   const selectAllPages = useCallback(() => {
      const allPages = new Set<number>();
      for (let i = 1; i <= numPages; i++) {
         allPages.add(i);
      }
      setSelectedPages(allPages);
   }, [numPages]);

   const deselectAllPages = useCallback(() => {
      setSelectedPages(new Set());
   }, []);

   const resetToAISuggestions = useCallback(() => {
      setSelectedPages(new Set(pagesKept));
   }, [pagesKeptKey]);

   const handleSaveDocument = useCallback(async (
      annotationType: AnnotationTypeCategory,
      quality: AnnotationQuality | undefined,
      verifyStamp: boolean,
      title: string,
      historyComment: string,
   ) => {
      if (!canWriteAnnotation) {
         return;
      }

      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      if (selectedPages.size === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No pages selected")));
         return;
      }

      const organizationId = params.organizationId || sessionState?.customerRef;
      const userId = params.userId || sessionState?.userRef;
      const userName = params.userName || sessionState?.userName;

      if (!organizationId || !userId || !userName) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }

      const hasSaveContext = params.contextType === AnnotationContextType.Ejendom
         ? Boolean(params.bfeNumber || params.documentId)
         : Boolean(params.documentId);

      if (!params.contextType || !hasSaveContext) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Missing context for saving annotation")));
         return;
      }
      if (!title.trim()) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Title is required")));
         return;
      }

      const selectedViewerPages = normalizePages(Array.from(selectedPages));
      const { aktPages: pagesToKeep, isSameAsExistingCut } = resolveAktPagesForSave({
         pdfSourceKind: params.pdfSourceKind,
         selectedPages: selectedViewerPages,
         sourceExtractedPages: params.sourceExtractedPages,
         cutPageCount: numPages || params.sourceExtractedPages?.length,
      });
      const normalizedAnnotationType = normalizeAnnotationTypeValue(annotationType);
      const isServitutTextDocument =
         params.contextType === AnnotationContextType.Servitut
         && (normalizedAnnotationType === "dokument" || normalizedAnnotationType === "servitutdokument");

      if (isServitutTextDocument && isSameAsExistingCut) {
         appMessageDispatch(actionSetInfoMessage(
            Localization.getText("Servitut text annotation is already saved")
         ));
         params.onDocumentSaved?.(pagesToKeep);
         params.onCloseSelectedPages?.(pagesToKeep);
         setShowSaveModal(false);
         return;
      }

      const persistDocument = async (overwriteTarget?: ANNAnnotationRecord, duplicatesToRemove: ANNAnnotationRecord[] = []) => {
         setSaving(true);
         try {
            // Cut using viewer-local page indices (1..N of the opened blob).
            const cutPdfBlob = await cutPdfToSelectedPages(params.pdfBlob!, selectedViewerPages);
            const filename = appendSuffixToPdfFileName(
               params.documentFilename || params.datoloebenr || params.aktId || "document.pdf",
               `_cut_${pagesToKeep.length}pages`,
            );
            const file = new File([cutPdfBlob], filename, { type: 'application/pdf' });
            const uploadResult = await uploadOverlayImage(file);
            const documentPayload = {
               filename,
               s3Key: uploadResult.key,
               s3Url: uploadResult.url,
               mimeType: 'application/pdf' as const,
               size: cutPdfBlob.size,
               extractedPages: pagesToKeep,
               provenance: overwriteTarget?.document?.provenance,
            };

            await saveAnnotation({
               type: AnnotationType.Document,
               documentId: params.contextType === AnnotationContextType.Servitut ? params.documentId : undefined,
               bfeNumber: params.contextType === AnnotationContextType.Ejendom
                  ? (params.bfeNumber ?? params.documentId)
                  : undefined,
               contextType: params.contextType!,
               organizationId,
               userId,
               userName,
               datoloebenr: params.datoloebenr,
               document: isServitutTextDocument
                  ? { ...documentPayload, llmAnalysis: undefined }
                  : mergeDocumentPayloadWithLlmAnalysis(
                     documentPayload,
                     buildDocumentLlmAnalysis(analysisItem, {
                        extractedPages: pagesToKeep,
                        resultsAccepted: true,
                     }),
                     overwriteTarget?.document,
                  ),
               title: title.trim(),
               historyComment: historyComment.trim() || Localization.getFormattedText("Extracted pages: {pages}", {
                  pages: pagesToKeep.join(", "),
               }),
               annotationType,
               quality,
               verified: canVerify && verifyStamp,
               id: overwriteTarget?.id,
               originalCreatedAt: overwriteTarget?.createdAt,
               originalCreatedBy: overwriteTarget?.createdBy,
               originalCreatedByName: overwriteTarget?.createdByName,
            });

            for (const duplicate of duplicatesToRemove) {
               if (!duplicate.id || duplicate.id === overwriteTarget?.id) {
                  continue;
               }
               try {
                  await deleteAnnotation(duplicate.id, organizationId);
               } catch (deleteErr) {
                  console.warn("Failed to delete duplicate servitut text annotation", deleteErr);
               }
            }

            appMessageDispatch(actionSetInfoMessage(
               Localization.getText("Document saved successfully")
            ));

            params.onDocumentSaved?.(pagesToKeep);
            params.onCloseSelectedPages?.(pagesToKeep);
            setShowSaveModal(false);
            editorDispatch({ type: ANNEditorActionType.SaveSuccess });
            windowDispatch(closeWindow(managedWindow.id));
         } catch (err: any) {
            appMessageDispatch(actionSetErrorMessage(
               err?.message || Localization.getText("Failed to save document")
            ));
         } finally {
            setSaving(false);
         }
      };

      if (isServitutTextDocument && params.documentId) {
         try {
            const existingItems = await ANNManager.listAnnotations(
               AnnotationContextType.Servitut,
               params.documentId,
               organizationId,
            );
            const existingServitutTexts = findAllServitutDocumentAnnotations(existingItems, params.datoloebenr);
            const overwriteTarget = existingServitutTexts[0];
            if (overwriteTarget) {
               const dialog = buildOverwriteServitutTextDialogMessage(overwriteTarget, pagesToKeep);
               windowDispatch(openWindow(WindowId.ConfirmationDialog, {
                  header: dialog.header,
                  children: dialog.children,
                  onSubmit: () => {
                     void persistDocument(overwriteTarget, existingServitutTexts.slice(1));
                  },
               }));
               return;
            }
         } catch (err: any) {
            appMessageDispatch(actionSetErrorMessage(
               err?.message || Localization.getText("Failed to save document")
            ));
            return;
         }
      }

      await persistDocument();
   }, [params, selectedPages, numPages, analysisItem, sessionState, appMessageDispatch, canWriteAnnotation, canVerify, editorDispatch, windowDispatch, managedWindow.id, saveAnnotation, deleteAnnotation]);

   const handleUploadToMap = useCallback(async (pageNumber: number) => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      setUploadingPage(pageNumber);
      try {
         const imageBlob = await renderPdfPageToImage(params.pdfBlob, pageNumber, 2.0);
         const filename = `${params.aktId || 'page'}_page${pageNumber}.png`;
         const file = new File([imageBlob], filename, { type: 'image/png' });
         await uploadOverlayImage(file);
         const dataUrl = await blobToDataUrl(imageBlob);
         const { coordinates } = await deriveOverlayPlacement({ blob: imageBlob }, mapitState.map?.getMapPop?.());
         const layerName = `${params.aktId || 'PDF'} - Page ${pageNumber}`;
         const newLayer = LayerFunc.createLayerInfoForImageOverlay(
            layerName,
            filename,
            dataUrl,
            coordinates
         );

         mapitStateDispatch(actionAddDataLayer(newLayer));

         appMessageDispatch(actionSetInfoMessage(
            Localization.getText("Page uploaded to map")
         ));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to upload page to map")
         ));
      } finally {
         setUploadingPage(null);
      }
   }, [params, mapitState.map, mapitStateDispatch, appMessageDispatch]);

   const handleDownloadCutPdf = useCallback(async () => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      if (selectedPages.size === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No pages selected")));
         return;
      }

      setSaving(true);
      try {
         const pagesToKeep = Array.from(selectedPages).sort((a, b) => a - b);
         const cutPdfBlob = await cutPdfToSelectedPages(params.pdfBlob, pagesToKeep);
         const filename = appendSuffixToPdfFileName(
            params.documentFilename || params.datoloebenr || params.aktId || "document.pdf",
            `_pages_${pagesToKeep.join('-')}`,
         );
         const url = URL.createObjectURL(cutPdfBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = filename;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(url);
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to download PDF")
         ));
      } finally {
         setSaving(false);
      }
   }, [params, selectedPages, appMessageDispatch]);

   const handleClose = useCallback(() => {
      if (numPages > 0) {
         const persisted = resolvePagesToPersistOnViewerClose({
            pdfSourceKind: params?.pdfSourceKind,
            selectedViewerPages: Array.from(selectedPages),
            sourceExtractedPages: params?.sourceExtractedPages,
            numPages,
            treatEmptyPagesKeptAsAll,
         });
         params?.onCloseSelectedPages?.(persisted);
      }
      windowDispatch(closeWindow(managedWindow.id));
   }, [params, selectedPages, numPages, treatEmptyPagesKeptAsAll, windowDispatch, managedWindow.id]);

   useEffect(() => {
      const root = displayRef.current;
      if (!root || numPages === 0) {
         return;
      }

      let raf = 0;
      const syncFromScroll = () => {
         const slot = pdfPageSlotHeight(estimatedPageHeightRef.current ?? 480);
         const page = pageFromScrollOffset(root.scrollTop, slot, numPages);
         if (!findNavLockRef.current) {
            setCurrentPage((prev) => (prev !== page ? page : prev));
         }
         const extra = scrollActivityRef.current ? 1 : 2;
         const windowSize = mountedWindowSizeForViewport(root.clientHeight, slot, extra);
         const next = new Set(mountedPagesAround(page, numPages, windowSize));
         if (pinnedFindPageRef.current) {
            next.add(pinnedFindPageRef.current);
         }
         setMountedPdfPages((prev) => (samePageSet(prev, next) ? prev : next));
      };

      let trailing = 0;
      const onScroll = () => {
         bumpScrollActivity();
         if (trailing !== 0) {
            window.clearTimeout(trailing);
         }
         trailing = window.setTimeout(() => {
            trailing = 0;
            syncFromScroll();
         }, 500);
         if (raf !== 0) {
            return;
         }
         raf = requestAnimationFrame(() => {
            raf = 0;
            syncFromScroll();
         });
      };

      root.scrollTop = scrollOffsetForPage(
         currentPageRef.current,
         pdfPageSlotHeight(estimatedPageHeightRef.current ?? 480),
      );
      root.addEventListener("scroll", onScroll, { passive: true });
      syncFromScroll();
      return () => {
         root.removeEventListener("scroll", onScroll);
         if (trailing !== 0) {
            window.clearTimeout(trailing);
         }
         if (raf !== 0) {
            cancelAnimationFrame(raf);
         }
         if (scrollIdleTimerRef.current != null) {
            clearTimeout(scrollIdleTimerRef.current);
            scrollIdleTimerRef.current = null;
         }
      };
   }, [numPages, pdfData, bumpScrollActivity]);

   useEffect(() => {
      const root = displayRef.current;
      if (!root || !estimatedPageHeight || numPages < 1) {
         return;
      }
      root.scrollTop = scrollOffsetForPage(currentPageRef.current, pdfPageSlotHeight(estimatedPageHeight));
   }, [estimatedPageHeight, numPages]);

   /** After placeholders exist, scroll to first AI-suggested page once. */
   useEffect(() => {
      if (loading || numPages === 0 || didInitialAiScrollRef.current) return;
      if (pagesKept.length === 0) {
         didInitialAiScrollRef.current = true;
         return;
      }
      const first = pagesKept[0];
      didInitialAiScrollRef.current = true;
      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            scrollToPdfPage(first, "auto");
         });
      });
   }, [loading, numPages, pagesKeptKey, scrollToPdfPage]);

   useEffect(() => {
      const root = displayRef.current;
      if (!root) {
         return;
      }
      const observer = new ResizeObserver(() => {
         if (zoomModeRef.current !== "fitWidth" || findNavLockRef.current) {
            return;
         }
         if (resizeFitTimerRef.current != null) {
            clearTimeout(resizeFitTimerRef.current);
         }
         resizeFitTimerRef.current = setTimeout(() => {
            resizeFitTimerRef.current = null;
            applyFitWidth();
         }, 160);
      });
      observer.observe(root);
      return () => {
         observer.disconnect();
         if (resizeFitTimerRef.current != null) {
            clearTimeout(resizeFitTimerRef.current);
            resizeFitTimerRef.current = null;
         }
      };
   }, [applyFitWidth, numPages, pdfData]);

   const applyFindHighlight = useCallback((item: {
      str?: string;
      itemIndex: number;
      pageNumber: number;
   }) => {
      return highlightTextItemHtml(
         item.str || "",
         rangesForTextItem(findMatchesRef.current, item.pageNumber, item.itemIndex, null),
      );
   }, [findResultId]);

   const onFindTextLayerRendered = useCallback(() => {
      syncFindHitClasses();
      if (!pendingFindRevealRef.current) {
         return;
      }
      if (revealCurrentFindHit()) {
         pendingFindRevealRef.current = false;
      }
   }, [revealCurrentFindHit, syncFindHitClasses]);

   useEffect(() => {
      syncFindHitClasses();
   }, [findMatchIndex, syncFindHitClasses]);

   if (!params || (!params.pdfUrl && !params.pdfBlob)) {
      return (
         <AdvancedDragModal
            window={managedWindow}
            title={Localization.getText("PDF Analyzer")}
            onClose={handleClose}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ width: "1120px", height: "800px", left: "5%", top: "5%" }}
         >
            <ADModalBody>
               <GlassInfo>
                  <div className="ann-pdf-noData">
                     {Localization.getText("No PDF data available")}
                  </div>
               </GlassInfo>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   const canSave = Boolean(
      canWriteAnnotation &&
      params.contextType &&
      (params.contextType === AnnotationContextType.Ejendom
         ? (params.bfeNumber || params.documentId)
         : params.documentId) &&
      selectedPages.size > 0
   );
   const defaultHistoryComment = analysisItem?.summary
      ? `${analysisItem.summary.substring(0, 200)}${analysisItem.summary.length > 200 ? '...' : ''}`
      : '';
   const defaultTitle = params?.documentFilename || params?.datoloebenr || params?.aktId || Localization.getText("Document");
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");
   const identityHeading = params.documentHeading?.trim() || "";
   const identityDato = params.datoloebenr?.trim() || "";

   const titleParts: string[] = [Localization.getText("PDF Analyzer")];
   if (identityDato) {
      titleParts.push(identityDato);
   } else if (params.aktId) {
      titleParts.push(params.aktId);
   }
   if (identityHeading) {
      titleParts.push(identityHeading);
   } else if (params.documentFilename) {
      titleParts.push(params.documentFilename);
   }
   const windowTitle = titleParts.join(" - ");
   const pageSlotHeight = pdfPageSlotHeight(estimatedPageHeight ?? 480);

   return (
      <AdvancedDragModal
         window={managedWindow}
         title={windowTitle}
         onClose={handleClose}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "1120px", height: "800px", left: "5%", top: "5%" }}
      >
         <ADModalBody>
            <div className="ann-pdf-viewer">
               <div className="ann-pdf-columns">
                  <div className="ann-pdf-leftColumn">
                     {identityDato || identityHeading ? (
                        <div className="ann-pdf-panel ann-pdf-servitutIdentity">
                           {identityDato ? (
                              <div className="ann-pdf-servitutIdentityDato">{identityDato}</div>
                           ) : null}
                           {identityHeading ? (
                              <div className="ann-pdf-servitutIdentityHeading">{identityHeading}</div>
                           ) : null}
                        </div>
                     ) : null}
                     <div className="ann-pdf-panel ann-pdf-panel--pageSelection">
                        <div className="ann-pdf-pageSelectionHeader">
                           <strong>{Localization.getText("Page Selection")}:</strong>
                           <SimpleButton onClick={selectAllPages}>{Localization.getText("All")}</SimpleButton>
                           <SimpleButton onClick={deselectAllPages}>{Localization.getText("None")}</SimpleButton>
                           {canRestoreAiPages ? (
                              <SimpleButton onClick={resetToAISuggestions}>{Localization.getText("Restore AI suggestion")}</SimpleButton>
                           ) : null}
                           <span className="ann-pdf-pageSelectionCount">
                              {selectedPages.size} / {numPages} {Localization.getText("pages")}
                           </span>
                        </div>
                        <PageSelectionGrid
                           ref={pageButtonListRef}
                           numPages={numPages}
                           selectedPages={selectedPages}
                           suggestedKey={canRestoreAiPages ? pagesKeptKey : ""}
                           suggestedPages={canRestoreAiPages ? pagesKept : []}
                           onJump={scrollToPdfPage}
                        />
                        <div className="ann-pdf-pageSelectionActions">
                           <SimpleButton
                              onClick={handleDownloadCutPdf}
                              disabled={saving || selectedPages.size === 0}
                              variant="default"
                           >
                              <MIBIcon icon="download" />
                              {Localization.getText("Download Selected")}
                           </SimpleButton>

                           {canWriteAnnotation ? (
                              <SimpleButton
                                 onClick={() => {
                                    if (params.saveAndApprove) {
                                       void handleSaveDocument(
                                          "Dokument",
                                          undefined,
                                          true,
                                          defaultTitle,
                                          defaultHistoryComment,
                                       );
                                       return;
                                    }
                                    setShowSaveModal(true);
                                 }}
                                 disabled={saving || !canSave}
                                 variant="default"
                              >
                                 <MIBIcon icon="checkmark" />
                                 {Localization.getText(params.saveAndApprove
                                    ? "Save and approve annotation"
                                    : "Save as Annotation")}
                              </SimpleButton>
                           ) : null}
                        </div>
                     </div>

                     {analysisItem ? (
                        <div className="ann-pdf-analysisWrap">
                           <ANNAnalysisResultPanel
                              analysisItem={analysisItem}
                              pagesKeptCount={pagesKept.length}
                              emptyMeansAll={treatEmptyPagesKeptAsAll}
                              mode={params?.analysisDisplayMode === "summary" ? "summary" : "full"}
                           />
                        </div>
                     ) : null}
                  </div>

                  <div className="ann-pdf-rightColumn">
                     <div className="ann-pdf-toolbar">
                        <div className="ann-pdf-toolbarGroup ann-pdf-toolbarFindRow">
                           <input
                              ref={findInputRef}
                              type="search"
                              className="ann-pdf-findInput"
                              value={findQuery}
                              onChange={(e) => setFindQuery(e.target.value)}
                              onKeyDown={(e) => {
                                 if (e.key === "Enter") {
                                    e.preventDefault();
                                    onFindSubmit();
                                 }
                              }}
                              placeholder={Localization.getText("Find in PDF")}
                              disabled={loading || numPages === 0}
                              aria-label={Localization.getText("Find in PDF")}
                           />
                           <SimpleButton
                              onClick={() => onFindSubmit("all")}
                              disabled={loading || numPages === 0 || findSearchStatus === "searching"}
                              variant="muted"
                              selected={findScope === "all" && findSearchStatus !== "idle"}
                              title={Localization.getText("Find")}
                           >
                              {Localization.getText("Find")}
                           </SimpleButton>
                           <SimpleButton
                              onClick={() => onFindSubmit("selected")}
                              disabled={loading || numPages === 0 || findSearchStatus === "searching" || selectedPages.size === 0}
                              variant="muted"
                              selected={findScope === "selected" && findSearchStatus !== "idle"}
                              title={Localization.getText("Search in selected")}
                           >
                              {Localization.getText("Search in selected")}
                           </SimpleButton>
                           <ControlButton
                              onClick={goFindPrev}
                              disabled={findMatches.length === 0 || findSearchStatus === "searching"}
                              title={Localization.getText("Find previous match")}
                           >
                              <MIBIcon icon="goLeft" />
                           </ControlButton>
                           <ControlButton
                              onClick={goFindNext}
                              disabled={findMatches.length === 0 || findSearchStatus === "searching"}
                              title={Localization.getText("Find next match")}
                           >
                              <MIBIcon icon="goRight" />
                           </ControlButton>
                           {findSearchStatus === "searching" ? (
                              <Spinner animation="border" size="sm" className="ann-pdf-miniSpinner" />
                           ) : null}
                           <span className="ann-pdf-findMeta">
                              {findSearchStatus === "searching" && findProgress
                                 ? `${Localization.getText("Searching PDF...")} ${findProgress.done}/${findProgress.total}`
                                 : findMatches.length > 0
                                    ? `${findMatchIndex + 1} / ${findMatches.length}`
                                    : findSearchStatus === "done" && findQuery.trim() && findMatches.length === 0
                                       ? Localization.getText("No matches in PDF")
                                       : null}
                              {findCapped ? ` · ${Localization.getText("PDF find limit reached")}` : ""}
                           </span>
                        </div>

                        <div className="ann-pdf-toolbarGroup">
                           <ControlButton
                              onClick={zoomOut}
                              title={Localization.getText("Zoom out")}
                           >
                              <MIBIcon icon="zoomOut" />
                           </ControlButton>
                           <span className="ann-pdf-toolbarValue">{Math.round(scale * 100)}%</span>
                           <ControlButton
                              onClick={zoomIn}
                              title={Localization.getText("Zoom in")}
                           >
                              <MIBIcon icon="zoomIn" />
                           </ControlButton>
                           <SimpleButton
                              onClick={applyFitWidth}
                              disabled={loading || numPages === 0}
                              variant="muted"
                              selected={zoomMode === "fitWidth"}
                              title={Localization.getText("Page width")}
                           >
                              {Localization.getText("Page width")}
                           </SimpleButton>
                        </div>

                        <div className="ann-pdf-divider" />

                        <div className="ann-pdf-toolbarGroup">
                           <ControlButton
                              onClick={goToPrevPage}
                              disabled={currentPage <= 1}
                              title={Localization.getText("Previous page")}
                           >
                              <MIBIcon icon="goLeft" />
                           </ControlButton>
                           <span className="ann-pdf-toolbarPageValue">
                              {currentPage} / {numPages}
                           </span>
                           <ControlButton
                              onClick={goToNextPage}
                              disabled={currentPage >= numPages}
                              title={Localization.getText("Next page")}
                           >
                              <MIBIcon icon="goRight" />
                           </ControlButton>
                        </div>

                        <div className="ann-pdf-divider ann-pdf-divider--wide" />

                        <SimpleButton
                           onClick={() => togglePageSelection(currentPage)}
                           variant={isSelectedPage(currentPage) ? 'success' : 'default'}
                        >
                           <MIBIcon icon="checkmark" />
                           {isSelectedPage(currentPage)
                              ? Localization.getText("Selected")
                              : Localization.getText("Select page")}
                        </SimpleButton>

                        <SimpleButton
                           onClick={() => handleUploadToMap(currentPage)}
                           disabled={uploadingPage === currentPage}
                        >
                           {uploadingPage === currentPage
                              ? <Spinner animation="border" size="sm" className="ann-pdf-miniSpinner" />
                              : <MIBIcon icon="addToMap" />}
                           {Localization.getText("Upload to Map")}
                        </SimpleButton>
                     </div>

                     <div className="ann-pdf-display" ref={displayRef}>
                        {loading && (
                           <div className="ann-pdf-loading">
                              <Spinner animation="border" role="status" />
                              <div className="ann-pdf-loadingText">{Localization.getText("Loading")}...</div>
                           </div>
                        )}

                        {error && (
                           <div className="ann-pdf-displayError">
                              {error}
                           </div>
                        )}

                        {pdfData && (
                           <Document
                              file={pdfData}
                              options={documentOptions}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={onDocumentLoadError}
                              loading={null}
                           >
                              <div
                                 className="ann-pdf-pagesStack ann-pdf-pagesStack--virtual"
                                 style={{
                                    ...(basePageWidth > 0
                                       ? {
                                          ["--ann-pdf-page-width" as string]: `${basePageWidth * scale}px`,
                                          width: `${basePageWidth * scale}px`,
                                       }
                                       : {}),
                                    height: numPages > 0
                                       ? virtualPdfStackHeight(numPages, pageSlotHeight)
                                       : undefined,
                                 }}
                              >
                                 {numPages > 0 &&
                                    Array.from(mountedPdfPages)
                                       .filter((pageNum) => pageNum >= 1 && pageNum <= numPages)
                                       .sort((a, b) => a - b)
                                       .map((pageNum) => {
                                       const suggested = isAISuggestedPage(pageNum);
                                       const selected = isSelectedPage(pageNum);
                                       const frameClass =
                                          suggested && selected
                                             ? "ann-pdf-pageFrame--suggestedSelected"
                                             : selected
                                                ? "ann-pdf-pageFrame--selected"
                                                : suggested
                                                   ? "ann-pdf-pageFrame--suggested"
                                                   : "ann-pdf-pageFrame--none";
                                       return (
                                          <div
                                             key={pageNum}
                                             className={`ann-pdf-pageFrame ${frameClass}`}
                                             data-ann-pdf-page={pageNum}
                                             style={{
                                                top: scrollOffsetForPage(pageNum, pageSlotHeight),
                                             }}
                                          >
                                             {suggested && (
                                                <div
                                                   className={`ann-pdf-suggestedBadge ${selected ? "ann-pdf-suggestedBadge--selected" : ""}`}
                                                >
                                                   {aiSuggestedLabel}
                                                </div>
                                             )}
                                             <Page
                                                pageNumber={pageNum}
                                                scale={scale}
                                                renderTextLayer={true}
                                                renderAnnotationLayer={true}
                                                customTextRenderer={findMatches.length > 0 ? applyFindHighlight : undefined}
                                                onRenderTextLayerSuccess={onFindTextLayerRendered}
                                             />
                                          </div>
                                       );
                                    })}
                              </div>
                           </Document>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            {canWriteAnnotation ? (
               <SaveDocumentModal
                  open={showSaveModal}
                  onClose={() => setShowSaveModal(false)}
                  onSave={handleSaveDocument}
                  saving={saving}
                  canVerify={canVerify}
                  defaultTitle={defaultTitle}
                  defaultHistoryComment={defaultHistoryComment}
               />
            ) : null}
         </ADModalBody>
      </AdvancedDragModal>
   );
}
